import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  IconButton,
  Button,
  Typography,
  Tooltip,
  FormHelperText,
  Dialog,
  DialogContent,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import TextBox from '../../components/TextBox';
import DatePickV2 from '../../components/DatePickV2';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import ButtonSubmit from '../../components/ButtonSubmit';
import { Add, AttachFile, DeleteOutline, Preview, Close } from '@mui/icons-material';
import TextCurrency from '../../components/TextCurrency';
import { SettlementsAPI } from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';
import { validatePaymentDocumentDraft } from './paymentDocumentValidation';

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000/').replace(/\/+$/, '/');

const absolutizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.replace(/^\/+/, '')}`;
};

const isImageLike = (f) => {
  if (!f) return false;
  const name = typeof f === 'string' ? f : f.name || '';
  const type = typeof f === 'string' ? '' : f.type || '';
  return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};

const isPdfLike = (f) => {
  if (!f) return false;
  const name = typeof f === 'string' ? f : f.name || '';
  const type = typeof f === 'string' ? '' : f.type || '';
  return type === 'application/pdf' || /\.pdf($|\?)/i.test(name);
};

const getPreviewUrl = (f) => {
  if (!f) return '';
  if (typeof f === 'string') return absolutizeUrl(f);
  try {
    return URL.createObjectURL(f);
  } catch {
    return '';
  }
};

function MiniFilePill({ file, onOpen, onDelete }) {
  const name = typeof file === 'string' ? (file.split('/').pop() || 'Archivo') : (file?.name || 'Archivo');

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.35,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(16,23,60,0.03)',
        maxWidth: '100%',
      }}
    >
      <Typography variant="caption" sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name}
      </Typography>
      <IconButton
        type="button"
        size="small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen?.();
        }}
        sx={{ width: 22, height: 22 }}
      >
        <Preview fontSize="inherit" />
      </IconButton>
      <IconButton
        type="button"
        size="small"
        color="error"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete?.();
        }}
        sx={{ width: 22, height: 22 }}
      >
        <DeleteOutline fontSize="inherit" />
      </IconButton>
    </Box>
  );
}

function FilePreviewDialog({ open, file, onClose }) {
  const url = file ? getPreviewUrl(file) : '';
  const isImg = isImageLike(file);
  const isPdf = isPdfLike(file);
  const name = typeof file === 'string' ? (file.split('/').pop() || 'Documento') : (file?.name || 'Documento');

  React.useEffect(() => {
    if (!file || typeof file === 'string') return;
    return () => {
      try {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      } catch {
        // noop
      }
    };
  }, [file, url]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: '#10173c', color: '#fff' }}>
        <Typography variant="subtitle2" sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {url && (
            <Button
              type="button"
              href={url}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              size="small"
              sx={{ textTransform: 'none', bgcolor: 'rgba(255,255,255,0.14)' }}
            >
              Abrir
            </Button>
          )}
          <IconButton type="button" onClick={onClose} sx={{ color: '#fff' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent
        dividers
        sx={{ p: 0, height: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isImg ? '#0b1026' : '#fafafa' }}
      >
        {isImg && url && (
          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        )}
        {isPdf && url && (
          <object data={url} type="application/pdf" width="100%" height="100%">
            <embed src={url} type="application/pdf" width="100%" height="100%" />
          </object>
        )}
        {!isImg && !isPdf && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2">
              No se puede previsualizar este tipo de archivo.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentDocumentForm({ setOpen, onSave, supplierBankAccounts = [], initialData }) {
  const [form, setForm] = useState({
    type: 'transferencia',
    isAch: false,
    reference: '',
    amount: 0,
    date: new Date(),
    bankAccount: null,
    files: [],
  });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [errors, setErrors] = useState({});
  const [refError, setRefError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setForm({
        type: initialData.type === 'ach' ? 'transferencia' : (initialData.type || 'transferencia'),
        isAch: Boolean(initialData.isAch || initialData.type === 'ach'),
        reference: initialData.reference || '',
        amount: Number(initialData.amount || 0),
        date: initialData.date || new Date(),
        bankAccount: initialData.bankAccount || null,
        files: Array.isArray(initialData.files) ? initialData.files : [],
      });
    }
  }, [initialData]);

  const isTransfer = /transfer/i.test(String(form.type || ''));
  const isAch = isTransfer && Boolean(form.isAch);
  const showReference = !/efectivo/i.test(String(form.type || ''));
  const referenceRequired = showReference && !isAch;

  const handleChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'type' && !/transfer/i.test(String(value || ''))
        ? { isAch: false }
        : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateRequiredFields = () => {
    const nextErrors = validatePaymentDocumentDraft(form);

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSnack({ open: true, message: 'Completa todos los campos obligatorios visibles.', severity: 'error' });
      return false;
    }

    return true;
  };

  const validateReference = async (val) => {
    if (!showReference) {
      setRefError('');
      return true;
    }

    const ref = String(val || '').trim();
    if (!ref) {
      if (!referenceRequired) {
        setRefError('');
        return true;
      }
      setRefError('Referencia requerida');
      return false;
    }

    try {
      await SettlementsAPI.checkRef(ref);
      setRefError('');
      return true;
    } catch (e) {
      const serverMsg = e?.response?.status === 409
        ? 'Error por medio de pago duplicado: esa referencia ya existe.'
        : 'No se pudo validar la referencia. Intenta de nuevo.';
      setRefError(serverMsg);
      setSnack({ open: true, message: serverMsg, severity: 'error' });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);

    try {
      const hasRequired = validateRequiredFields();
      if (!hasRequired) return;

      const okRef = await validateReference(form.reference);
      if (!okRef) return;

      const amountNum = Number(form.amount) || 0;
      if (amountNum <= 0) {
        setErrors((prev) => ({ ...prev, amount: 'Monto invalido (debe ser > 0)' }));
        setSnack({ open: true, message: 'Monto invalido (debe ser > 0).', severity: 'error' });
        return;
      }

      onSave({
        type: form.type,
        isAch,
        reference: String(form.reference || '').trim(),
        amount: amountNum,
        date: form.date,
        bankAccount: form.bankAccount,
        files: form.files || [],
      });

      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilePick = (event) => {
    const selected = Array.from(event?.target?.files || []);
    if (!selected.length) return;
    setForm((prev) => ({ ...prev, files: [...(prev.files || []), ...selected] }));
    setErrors((prev) => ({ ...prev, files: '' }));
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setForm((prev) => ({
      ...prev,
      files: (prev.files || []).filter((_, i) => i !== index),
    }));
  };

  const openFilePreview = (fileOrUrl) => {
    setPreviewFile(fileOrUrl);
    setPreviewOpen(true);
  };

  const referenceError = errors.reference || refError;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 380,
        m: '0 auto',
        '& .MuiInputBase-input': { color: 'text.primary' },
        '& .MuiInputLabel-root': { color: 'text.secondary' },
        '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
        '& .MuiSelect-select': { color: 'text.primary' },
      }}
    >
      {showReference && (
        <TextBox
          label="Referencia / No. documento"
          name="reference"
          onChange={handleChange}
          width={'300px'}
          required={referenceRequired}
          newValue={form.reference}
          externalError={Boolean(referenceError)}
          externalHelperText={referenceError || ''}
        />
      )}

      <TextCurrency
        required
        label="Monto"
        name="amount"
        onChange={handleChange}
        sx={{ mb: 2 }}
        newValue={form.amount}
        matchTextBox
        width={'300px'}
      />
      {errors.amount ? (
        <Typography variant="caption" color="error" sx={{ width: '300px', mt: -1, mb: 1 }}>
          {errors.amount}
        </Typography>
      ) : null}

      <FormControl fullWidth sx={{ width: '300px', mb: 1 }} size="small" error={Boolean(errors.type)}>
        <InputLabel id="payment-type-label">Tipo</InputLabel>
        <Select
          labelId="payment-type-label"
          label="Tipo"
          value={form.type}
          onChange={(e) => handleChange('type', e.target.value)}
        >
          <MenuItem value="transferencia">Transferencia</MenuItem>
          <MenuItem value="cheque">Cheque</MenuItem>
          <MenuItem value="efectivo">Efectivo</MenuItem>
          <MenuItem value="abasto">Abasto</MenuItem>
        </Select>
        {errors.type ? <FormHelperText>{errors.type}</FormHelperText> : null}
      </FormControl>

      {isTransfer && (
        <Box sx={{ width: '300px', mb: 1 }}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={Boolean(form.isAch)}
                onChange={(event) => handleChange('isAch', event.target.checked)}
              />
            )}
            label="Transferencia ACH / Otro banco"
          />
          {isAch && (
            <Alert severity="info" sx={{ mt: 0.5, py: 0.25 }}>
              Algunos bancos no generan número de referencia para transferencias ACH.
              El comprobante es obligatorio.
            </Alert>
          )}
        </Box>
      )}

      <Stack direction="row" alignItems="center" sx={{ width: '300px', gap: 0.5 }}>
        <DatePickV2
          name="date"
          label="Fecha"
          value={form.date}
          onChange={handleChange}
          width={'250px'}
          size={'small'}
          allowHistoricalDates={true}
          allowFutureDates={false}
        />
        <Tooltip title="Adjuntar archivos">
          <IconButton
            type="button"
            size="small"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              fileInputRef.current?.click();
            }}
          >
            <AttachFile fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          hidden
          multiple
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
          onChange={handleFilePick}
        />
      </Stack>
      {errors.date ? (
        <Typography variant="caption" color="error" sx={{ width: '300px', mb: 1 }}>
          {errors.date}
        </Typography>
      ) : null}

      {showReference && (
        <>
          <AutoCompleteBank
            name={'bankAccount'}
            label={'Cuenta bancaria'}
            options={supplierBankAccounts}
            getOptionLabel={(opt) => {
              if (!opt) return '';
              const bankName = opt.bankName || opt.bank || 'Banco';
              const accNumber = opt.accNumber || opt.accountNumber || '';
              const accType = opt.accType || opt.type || '';
              return `${bankName}: ${accNumber} (${accType})`;
            }}
            onChange={(name, value) => handleChange(name, value)}
            value={form.bankAccount}
            sx={{ width: '300px' }}
          />
          {errors.bankAccount ? (
            <Typography variant="caption" color="error" sx={{ width: '300px', mt: -1, mb: 1 }}>
              {errors.bankAccount}
            </Typography>
          ) : null}
        </>
      )}

      {(form.files || []).length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ width: '300px', mt: 0.5, mb: 0.5 }}>
          {(form.files || []).map((file, idx) => (
            <MiniFilePill
              key={`${typeof file === 'string' ? file : file.name}-${idx}`}
              file={file}
              onOpen={() => openFilePreview(file)}
              onDelete={() => handleRemoveFile(idx)}
            />
          ))}
        </Stack>
      )}
      {errors.files ? (
        <Typography variant="caption" color="error" sx={{ width: '300px', mt: 0.5 }}>
          {errors.files}
        </Typography>
      ) : null}

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1 }}>
        <ButtonSubmit
          name={submitting ? 'Validando...' : 'Agregar'}
          icon={Add}
          loading={submitting}
          disabled={submitting}
        />
      </Box>

      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack((prev) => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />

      <FilePreviewDialog
        open={previewOpen}
        file={previewFile}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
      />
    </Box>
  );
}
